import { AlertCircle, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";
import { useContext, useEffect, useState } from "react";
import { Rating } from "@mui/material";
import Pagination from "../Pagination";
import AnimatedComponent from "components/reusable/AnimatedComponent";
import { BiSolidFileExport } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";

const alerts = [
  {
    type: "error",
    icon: <AlertCircle className="text-red-500" size={16} />,
    message: "3 drivers below 4.0 rating",
  },
  {
    type: "success",
    icon: <CheckCircle className="text-green-500" size={16} />,
    message: "Customer satisfaction trending up",
  },
];


export default function FeedbackDashboard() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const { t } = useTranslation()
  const { updatePageName } = useContext(AuthContext)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [dataSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });
  const [feedbackData, setFeedbackData] = useState()
  const ratings = [
    { category: "Professionalism", score: 0 },
    { category: "Service", score: 0 },
    { category: "Communication", score: 0 },
  ];


  useEffect(() => {
    updatePageName(t('FEEDBACK_MANAGER'))
  }, [])

  const [feedbackRating, setFeedbackRating] = useState(ratings)
  const allFeedback = async (data) => {
    try {
      const payload = {
        page,
        pageSize,
        sortBy: dataSort.sortBy,
        sortType: dataSort.sortType,
         timezone: new Date().getTimezoneOffset(),
      };
      const path = apiPath.getFeedbackList;
      const result = await apiGet(path, payload);
      const responseData = result?.data?.results;
      const resultStatus = result?.data?.success;
      
      if (data == "export") {
        helpers.downloadFile(responseData?.filePath);
      } else {
        setFeedbackData(responseData);
        setFeedbackRating([
          { category: "Professionalism", score: helpers.orOperator(responseData?.ratingData?.avgProfessionalismRating, 0) },
          { category: "Service", score: helpers.orOperator(responseData?.ratingData?.avgServiceRating, 0) },
          { category: "Communication", score: helpers.orOperator(responseData?.ratingData?.avgCommunicationRating, 0) },

        ])
      }
      setPaginationObj({
        ...paginationObj,
        page: resultStatus ? result?.data?.results.page : null,
        pageCount: resultStatus ? result?.data?.results.totalPages : null,
        perPageItem: resultStatus ? responseData?.docs?.length : null,
        totalItems: resultStatus ? result?.data?.results.totalDocs : null,
      });
    } catch (error) {
      console.error("error in get all sub admin list==>>>>", error.message);
    }
  };
  useEffect(() => {
    allFeedback();
  }, [page, pageSize]);

  const dynamicPage = (e) => {
    const newPage = e?.selected + 1;
    setPage(newPage);
  };


  return (

    <div>
      <motion.div
        className="p-6 space-y-8 bg-gradient-to-br from-gray-50 to-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
       
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.15,
              },
            },
          }}

          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
          <motion.div
            className="rounded-2xl shadow-md bg-white p-6"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0 },
            }}
            whileHover={{ scale: 1.015 }}
            transition={{ type: "spring", stiffness: 120 }}
          >
            <p className="text-gray-500">Overall Rating</p>
            <div className="text-5xl font-extrabold text-yellow-500 mt-2">{helpers.orOperator(feedbackData?.ratingData?.avgMeanRating?.toFixed(2), 0)} <Rating name="read-only" value={helpers.orOperator(feedbackData?.ratingData?.avgMeanRating, 0)} precision={0.1} readOnly /></div>
            <p className="text-sm text-gray-400 mt-1">{helpers.orOperator(feedbackData?.ratingData?.totalCount, 0)} reviews</p>
          </motion.div>

          <motion.div
            className="rounded-2xl shadow-md bg-white p-6 space-y-3"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0 },
            }}
            whileHover={{ scale: 1.015 }}
            transition={{ type: "spring", stiffness: 120 }}

          >
            <p className="text-gray-500">Category Ratings</p>
            {feedbackRating?.map((r) => (
              <div key={r?.category} className="flex justify-between text-sm font-medium">
                <span className="text-gray-700">{r?.category}:</span>
                <span className="text-yellow-500 flex gap-2 items-center"><span> {helpers.orOperator(r?.score?.toFixed(2), 0)}</span> <Rating name="read-only" value={helpers.orOperator(r?.score, 0)} precision={0.1} readOnly /></span>
              </div>
            ))}
          </motion.div>

        </motion.div>

        <motion.div
          className="space-y-4"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.15,
              },
            },
          }}
        >
          <h3 className="text-xl font-semibold border-b border-purple-500 pb-1 w-fit">
            Customer Reviews
          </h3>
            <div className="flex justify-end mt-4">
              {feedbackData?.docs?.length > 0 && (
                <button type="button" title={t("EXPORT_CSV")} className="bg-gradientTo text-sm flex gap-2 px-8 ml-3  py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto "
                  onClick={() => allFeedback("export")} >
                  <BiSolidFileExport size={18} />
                  {t("EXPORT_CSV")}
                </button>

              )}
              </div>
          <div className="max-h-[305px] min-h-[210px] overflow-y-auto ">
            <div className="grid gap-4 md:grid-cols-2 ">
              {feedbackData?.docs?.map((review) => (
                <motion.div
                  key={review?._id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0 },
                  }}

                  transition={{ type: "spring", stiffness: 120 }}
                  className="rounded-xl bg-white shadow-md p-5 space-y-3"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{helpers.orOperator(helpers.capitalizeFirstWord(review?.userDetails?.fullName), 'N/A')}</p>
                      <p className="font-semibold text-xs text-gray-500">{t('DRIVER')}: {helpers.capitalizeFirstWord(review?.driverName)}</p>
                        <p className="font-semibold text-xs text-gray-500">Booking ID: #{review?.bookingDetails?.bookingId || 'N/A'}</p>
                      <p className="text-xs text-gray-500"> {helpers.getDateAndTime(review?.createdAt)}</p>
                    </div>
                    <span className="text-yellow-500 text-lg font-bold flex gap-2 items-center"><span> {helpers.orOperator(review?.meanRating?.toFixed(2), 0)}</span> <Rating name="read-only" value={helpers.orOperator(review?.meanRating, 0)} precision={0.1} readOnly /></span>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    {[
                      { category: "Professionalism", score: helpers.orOperator(review?.professionalismRating, 0) },
                      { category: "Service", score: helpers.orOperator(review?.serviceRating, 0) },
                      { category: "Communication", score: helpers.orOperator(review?.communicationRating, 0) },
            
                    ]?.map((r) => (
                      <span
                        key={r?.category}
                        className="bg-gray-100 px-3 py-1 rounded-full text-gray-700 flex items-center gap-1"
                      >
                        {r?.category}: <span className="text-yellow-500 font-semibold">{helpers.orOperator(r?.score?.toFixed(2), 0)}</span>
                      </span>
                    ))}
                  </div>

                  {/* <p className="text-sm text-gray-600 leading-relaxed">Feedback: {helpers.orOperator(review?.feedback, 'N/A')}</p> */}
                  <p className="text-sm font-semibold text-gray-600 mb-1">Feedback:</p>
                    <span className="text-sm text-gray-500 leading-relaxed whitespace-pre-line"> {helpers.orOperator(review?.feedback, 'N/A')}</span>
                </motion.div>
              ))}

            </div>
            {helpers.andOperator(feedbackData?.docs?.length == 0, <div className="flex justify-center items-center h-[300px]">No records found</div>)}
          </div>
        </motion.div>
      </motion.div>
      <AnimatedComponent>
        {paginationObj?.totalItems ? <Pagination handlePageClick={dynamicPage} options={paginationObj} page={page} /> : null}
      </AnimatedComponent>

    </div>
  );
}